import { Combobox, CurrencyInput, EInputTypes, InputColumnTableProps, InputTable, InputTextCustom, formatCurrency, getDate } from "medere-ui-framework"
import '../Budget.scss'
import { getNomenclatorValue, getProfessionalActivities } from "../../../servicesHooks/Budget/useBudgetService";
import { ProfessionalActivityBudgetDetailDTO, defaultProfessionalActivityBudgetDetailDTO, getSubtotalValueDay, getSubtotalValueMonth } from "../../../dto/budget/BudgetDetailDTO";
import { useIntl } from "react-intl";
import { NomenclatorDTO } from "../../../dto/NomenclatorDTO";
import { useEffect, useState } from "react";
import { InputTableContainerProps } from "../../../dto/InputTableContainerProps";
import React from "react";
import { BudgetBodyComponent } from "./BudgetBodyComponent";

interface ProfessionalActivityTableProps extends InputTableContainerProps {
    items: ProfessionalActivityBudgetDetailDTO[];
    date?: Date;
    healthEntity?: number;
    healthEntityPlan?: number;
  }
  
  export default function ProfessionalActivityTable({items, addToTotal, duration, CRUDBehaviour=true,showPriceByItem=true, showEarnings, date, healthEntity, healthEntityPlan}:ProfessionalActivityTableProps) {
    
    const intl = useIntl();
    
    const professionalActivitySelectionEnabled = healthEntityPlan && (healthEntityPlan > 0) && date
    const [editCostEnabled, setEditCostEnabled] = useState<boolean>(false);
    
    const [newProfessionalActivityBudgetDetail, setNewProfessionalActivityBudgetDetail] = useState<ProfessionalActivityBudgetDetailDTO>(defaultProfessionalActivityBudgetDetailDTO);
    
    const [subtotalValueDay, setSubtotalValueDay] = useState<number>(0);
    const [subtotalValueMonth, setSubtotalValueMonth] = useState<number>(0);
    
  
    const calculateTotal = (hoursbymonth:number,visitsbymonth:number,unitprice:number) => {
      if(hoursbymonth && visitsbymonth)
        throw new Error("No se puede calcular el valor si se ingresan horas y visitas, se calculan solo con horas");
      if(hoursbymonth)
        return hoursbymonth*unitprice;
      return visitsbymonth*unitprice;
    }

    function calculateItem(item:ProfessionalActivityBudgetDetailDTO):ProfessionalActivityBudgetDetailDTO {
      let totalMonth = calculateTotal(item.hoursbymonth,item.visitsbymonth,item.unitprice);
      return {
        ...item,
        hoursbymonth: item.hoursbymonth ?? 0,
        visitsbymonth: item.visitsbymonth ?? 0,
        valueday: duration?((duration>0)?(totalMonth/duration):0):0,
        valuemonth: totalMonth
      }
    }

    useEffect(() => {
      //Seteo el valor dia actualizado para el input
      setNewProfessionalActivityBudgetDetail({...newProfessionalActivityBudgetDetail,valueday: duration?((duration>0)?newProfessionalActivityBudgetDetail.valuemonth/duration:0):0});
      let subtotal = 0;
      items.forEach((item) => {
        item.valuemonth = calculateTotal(item.hoursbymonth,item.visitsbymonth,item.unitprice);
        item.valueday = duration?((duration>0)?item.valuemonth/duration:0):0;
        subtotal += item.valuemonth;
      });
      //Vuelvo a calcular el subtotal valor mes (y con eso se actualiza el subtotal valor dia)
      setSubtotalValueMonth(subtotal);
    },[items, duration]);
    
    useEffect(() => {
      if(newProfessionalActivityBudgetDetail.nomenclator > 0 &&  professionalActivitySelectionEnabled)
        getNomenclatorValue(newProfessionalActivityBudgetDetail.nomenclator ?? -1, getDate(date) ?? undefined, healthEntityPlan).then((value:number) => {
          setEditCostEnabled(!value);
          setNewProfessionalActivityBudgetDetail({
            ...newProfessionalActivityBudgetDetail, 
            unitprice: value ?? 0
          });
        });
    },[newProfessionalActivityBudgetDetail.nomenclator, date, healthEntityPlan]);

    //Cuando cambia algun valor que actualiza el valor mes, lo actualizo
    useEffect(() => {
      setNewProfessionalActivityBudgetDetail({
        ...newProfessionalActivityBudgetDetail,
        valuemonth: calculateTotal(
          newProfessionalActivityBudgetDetail.hoursbymonth,
          newProfessionalActivityBudgetDetail.visitsbymonth,
          newProfessionalActivityBudgetDetail.unitprice
        )});
    },[newProfessionalActivityBudgetDetail.unitprice,newProfessionalActivityBudgetDetail.hoursbymonth,newProfessionalActivityBudgetDetail.visitsbymonth]);

    //Cuando cambia el valor mes, actualizo el dia con duration
    useEffect(() => {
      setNewProfessionalActivityBudgetDetail({...newProfessionalActivityBudgetDetail,valueday: duration?((duration>0)?newProfessionalActivityBudgetDetail.valuemonth/duration:0):0});
    },[newProfessionalActivityBudgetDetail.valuemonth, duration]);

    //Cuando cambia el subtotal del valor mes, actualizo el dia con la duration actual
    useEffect(() => {
      setSubtotalValueDay(duration?((duration>0)?subtotalValueMonth/duration:0):0);
    },[subtotalValueMonth, duration]);
    
    const columnsTable: InputColumnTableProps<ProfessionalActivityBudgetDetailDTO>[] = React.useMemo(() => [{
        body: (item) => <span className="nv-truncate nv-tip" data-pr-tooltip={item.description}>{item.description}</span>,
        className: "columnStart",
        header: "Prácticas",
        input: (_item) => 
          <Combobox 
            getItems={getProfessionalActivities}
            params={{ healthEntity, orderByNomenclatorType:true}}
            optionLabel="codeanddescription"
            placeholder={professionalActivitySelectionEnabled?"Seleccionar":"Fecha y Convenio obligatorios"}
            minPanelWidth={600}
            maxPanelWidth={800}
            setValue={(e:NomenclatorDTO) => {
              if(e) {
                setNewProfessionalActivityBudgetDetail({
                  ...newProfessionalActivityBudgetDetail, 
                  nomenclator: e.nomenclator ?? -1,
                  description: e.codeanddescription ?? '',
                })               
              }
            }} 
            value={newProfessionalActivityBudgetDetail.description} 
            disable={!professionalActivitySelectionEnabled}
          />,
        footer: "Subtotales",
        footerClassName: "font-bold",
      }, {
        header: "Horas por mes",
        className: "tableColumn justify-content-center",
        input: (_item) => 
          <InputTextCustom
            value={newProfessionalActivityBudgetDetail.hoursbymonth}
            inputType={EInputTypes.NUMBER}
            inputClassName="text-center"
            onChange={(e: any) => {
              setNewProfessionalActivityBudgetDetail({ 
                ...newProfessionalActivityBudgetDetail, 
                hoursbymonth: e.target.value ?? 0,
                visitsbymonth:0
              });
            }}
          />,
          body: (item, index) => <BudgetBodyComponent CRUDBehaviour={CRUDBehaviour} inputType={EInputTypes.NUMBER} value={item.hoursbymonth} inputClassName="text-center" onChange={(e: any) => {
            onHandleEditBudget(index, calculateItem({...item, hoursbymonth:e.target.value ?? 0, visitsbymonth:0}));
          }}/> 
      }, {
        className: "tableColumn justify-content-center",
        header: "Visitas por mes",
        input: (_item) => 
          <InputTextCustom
            value={newProfessionalActivityBudgetDetail.visitsbymonth}
            inputType={EInputTypes.NUMBER}
            inputClassName="text-center"
            onChange={(e: any) => {
              setNewProfessionalActivityBudgetDetail({ 
                ...newProfessionalActivityBudgetDetail, 
                hoursbymonth:0,
                visitsbymonth: e.target.value ?? 0,
              });
            }} 
          />,
        body: (item, index) => 
          <BudgetBodyComponent CRUDBehaviour={CRUDBehaviour} inputType={EInputTypes.NUMBER} value={item.visitsbymonth} inputClassName="text-center" onChange={(e: any) => {
            onHandleEditBudget(index, calculateItem({...item, visitsbymonth:e.target.value ?? 0, hoursbymonth:0}));
          }}/> 
      }, {
        className: "columnPrice w-10",
        
        header: <p className="mr-2">{showEarnings?"Costo /Convenio":"Valor unitario"}</p>,
        input: (_item) => 
          <React.Fragment>
            {!editCostEnabled && 
            <p className="mr-2">{formatCurrency(newProfessionalActivityBudgetDetail.unitprice as number)}</p>}
            {editCostEnabled &&
            <CurrencyInput ammount={newProfessionalActivityBudgetDetail.unitprice} setAmmount={(newValue: number | null) => {
              setNewProfessionalActivityBudgetDetail({ 
                ...newProfessionalActivityBudgetDetail, 
                unitprice: newValue ?? newProfessionalActivityBudgetDetail.unitprice ?? 0,
              });
            }}/> }
          </React.Fragment>,

        bodyClassName:"justify-content-end",
        body: (item, index) => 
          <BudgetBodyComponent CRUDBehaviour={CRUDBehaviour} currency value={item.unitprice} pClassName="mr-2" pValue={showPriceByItem?formatCurrency(item.unitprice as number):''} onChange={(newValue: number | null) => {
            onHandleEditBudget(index, calculateItem({...item, unitprice:newValue ?? 0}));
          }} />         
      }, {
        className: "columnPrice w-10",
        header: "Valor día",
        input: (_item) => <p>{formatCurrency(newProfessionalActivityBudgetDetail.valueday as number)}</p>,
        bodyClassName:"font-bold justify-content-end",
        body: (item) => showPriceByItem?formatCurrency(item.valueday as number):'',
        footerClassName:"font-bold",
        footer: formatCurrency(subtotalValueDay as number),
        show: () => duration > 0
      },{
        className: "columnPrice w-10 pr-2",
        header: "Valor total",
        input: (_item) => <p className="flex align-bottom">{formatCurrency(newProfessionalActivityBudgetDetail.valuemonth as number)}</p>,
        bodyClassName:"font-bold justify-content-end",
        body: (item) => showPriceByItem?formatCurrency(item.valuemonth as number):'',
        footerClassName:"font-bold",
        footer: formatCurrency(subtotalValueMonth as number) 
      }
    ], [duration, newProfessionalActivityBudgetDetail, subtotalValueDay, subtotalValueMonth, showPriceByItem, professionalActivitySelectionEnabled, editCostEnabled]);

  function onHandleAddBudget(): void {
    if(newProfessionalActivityBudgetDetail.nomenclator === -1) return;
    if(newProfessionalActivityBudgetDetail.hoursbymonth === 0 && newProfessionalActivityBudgetDetail.visitsbymonth === 0) return;
    items.push({...newProfessionalActivityBudgetDetail, visitsbymonth: newProfessionalActivityBudgetDetail.visitsbymonth ?? 0, hoursbymonth: newProfessionalActivityBudgetDetail.hoursbymonth ?? 0});
    const updatedValueMonth = getSubtotalValueMonth(items);
    if(addToTotal)
      addToTotal(updatedValueMonth - subtotalValueMonth)
    setSubtotalValueMonth(updatedValueMonth); 
    setSubtotalValueDay(getSubtotalValueDay(items));
    setNewProfessionalActivityBudgetDetail(defaultProfessionalActivityBudgetDetailDTO);
  }
  
  function onHandleDeleteBudget(index:number): void {
    // Remueve el item del array con el indice index
    items.splice(index, 1);
    const updatedValueMonth = getSubtotalValueMonth(items);
    if(addToTotal)
      addToTotal(updatedValueMonth - subtotalValueMonth)
    setSubtotalValueMonth(updatedValueMonth); 
    setSubtotalValueDay(getSubtotalValueDay(items));
  }

  //Recibe el índice correspondiente al item a editar, y el nuevo valor del item
  function onHandleEditBudget(index:number, newItemValue:ProfessionalActivityBudgetDetailDTO): void {

    //Calculo el valor-mes a agregar a subtotales y total.
    const valueMonthToAdd = newItemValue.valuemonth - items[index].valuemonth;
   
    //Actualiza valor del item con el nuevo
    items.splice(index, 1, newItemValue); 

    //Actualiza los subtotales
    const updatedValueMonth = subtotalValueMonth + valueMonthToAdd;
    setSubtotalValueDay(duration?((duration>0)?(updatedValueMonth/duration):0):0);
    setSubtotalValueMonth(updatedValueMonth);

    //Actualiza el total y refresca la tabla
    if(addToTotal) addToTotal(valueMonthToAdd);  
  }

    return (
        <div className="nv-card budget-table-card">
            <span className="tableTitle"> Actividad profesional </span>
            <InputTable divider dividerClassName="my-0 border-top-1" dividerInFirstItem
            columns={columnsTable} items={items}  onAdd={CRUDBehaviour?onHandleAddBudget:undefined} onDelete={CRUDBehaviour ? (_item, index) => onHandleDeleteBudget(index) : undefined} headerDivClassName="tableHeader align-items-center" inputDivClassName={"align-items-center"} footerDivClassName="tableFooter align-items-center" divBtnClassName="max-w-54 min-w-54"/>
        </div>
    )
}
